import type { RecommendedIntegration } from "./integrationCategories";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

import { RECOMMENDED_INTEGRATIONS } from "./integrationCategories";

const AUTO_SLIDE_INTERVAL_MS = 5_000;

const RecommendedCarousel: React.FC = () => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const [activeIndex, setActiveIndex] = React.useState(0);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % RECOMMENDED_INTEGRATIONS.length);
    }, AUTO_SLIDE_INTERVAL_MS);
  }, []);

  React.useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [resetTimer]);

  const goToSlide = (index: number) => {
    setActiveIndex(index);
    resetTimer();
  };

  const goToPrev = () => {
    goToSlide(
      (activeIndex - 1 + RECOMMENDED_INTEGRATIONS.length) %
        RECOMMENDED_INTEGRATIONS.length,
    );
  };

  const goToNext = () => {
    goToSlide((activeIndex + 1) % RECOMMENDED_INTEGRATIONS.length);
  };

  const goToDetail = (item: RecommendedIntegration) => {
    navigate(
      generatePath(routes.shops.integrations.detail, {
        projectId: projectId ?? "",
        integrationType: item.integrationType,
      }),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium text-default">Recommended</span>

      {/* Sliding viewport */}
      <div className="relative w-full h-[320px] rounded-xl overflow-hidden">
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{
            width: `${RECOMMENDED_INTEGRATIONS.length * 100}%`,
            transform: `translateX(-${(activeIndex * 100) / RECOMMENDED_INTEGRATIONS.length}%)`,
          }}
        >
          {RECOMMENDED_INTEGRATIONS.map((item) => {
            const config = INTEGRATION_CONFIGS[item.integrationType];
            return (
              <div
                key={item.integrationType}
                className={twMerge(
                  "relative h-full cursor-pointer overflow-hidden rounded-xl",
                  !item.image && "bg-gradient-to-br",
                  !item.image && item.gradient,
                )}
                style={{ width: `${100 / RECOMMENDED_INTEGRATIONS.length}%` }}
                onClick={() => goToDetail(item)}
              >
                {/* Use image if provided, otherwise use gradient */}
                {item.image ? (
                  <img
                    src={item.image}
                    alt={config.title}
                    className="absolute inset-0 z-0 h-full w-full object-cover"
                  />
                ) : (
                  <>
                    {/* Decorative circles - only show with gradient */}
                    <div className="absolute -right-12 -top-12 size-[280px] rounded-full bg-white/10" />
                    <div className="absolute -right-4 bottom-0 size-[200px] rounded-full bg-white/5" />
                  </>
                )}

                {/* Bottom overlay with integration info */}
                <div
                  className="absolute inset-x-0 bottom-0 z-10 p-5 pt-12 rounded-b-xl"
                  style={{
                    background:
                      "linear-gradient(to top, white 0%, rgba(255, 255, 255, 0.98) 15%, rgba(255, 255, 255, 0.92) 30%, rgba(255, 255, 255, 0.82) 45%, rgba(255, 255, 255, 0.65) 60%, rgba(255, 255, 255, 0.45) 75%, rgba(255, 255, 255, 0.25) 85%, rgba(255, 255, 255, 0.10) 92%, transparent 100%)",
                  }}
                >
                  <div className="flex items-end gap-3">
                    <img
                      src={config.image}
                      alt={config.title}
                      className="size-11 rounded-lg object-contain bg-white p-1.5 shadow-sm shrink-0"
                    />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-sm font-semibold text-default">
                        {config.title}
                      </span>
                      <span className="text-xs text-muted leading-relaxed line-clamp-1">
                        {item.subtitle}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Logo strip — logos on the left, arrows on the right */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {RECOMMENDED_INTEGRATIONS.map((item, index) => {
            const config = INTEGRATION_CONFIGS[item.integrationType];
            const isActive = index === activeIndex;

            return (
              <button
                key={item.integrationType}
                type="button"
                aria-label={`View ${config.title}`}
                className={twMerge(
                  "flex items-center gap-2 rounded-lg px-3 py-2 transition-all duration-300",
                  !isActive && "hover:opacity-70",
                )}
                onClick={() => goToSlide(index)}
              >
                <img
                  src={config.image}
                  alt={config.title}
                  className={twMerge(
                    "rounded-md object-contain transition-all duration-300",
                    isActive
                      ? "size-6 grayscale"
                      : "size-5 grayscale opacity-40",
                  )}
                />
                {isActive && (
                  <span className="text-xs font-medium text-default whitespace-nowrap">
                    {config.title}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1">
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={goToPrev}
            aria-label="Previous integration"
          >
            <ChevronLeft size={14} />
          </Button>
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={goToNext}
            aria-label="Next integration"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecommendedCarousel;
